import React from 'react';

/**
 * SwiftUI Preview Component
 * Displays a visual representation of SwiftUI code
 * Note: This is a simplified preview - full SwiftUI rendering would require a Swift runtime
 */
const SwiftUIPreview = ({ swiftCode }) => {
  if (!swiftCode || !swiftCode.trim()) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-900 text-gray-500">
        <div className="text-center">
          <p className="text-sm">No code to preview</p>
        </div>
      </div>
    );
  }

  // Extract view structure from SwiftUI code for basic visualization
  const extractViewInfo = (code) => {
    const structMatch = code.match(/struct\s+(\w+)\s*:/);
    const structName = structMatch ? structMatch[1] : 'View';
    
    // Extract buttons
    const buttonMatches = code.matchAll(/Button\s*\(\s*"([^"]+)"\s*\)/g);
    const buttons = Array.from(buttonMatches).map(m => m[1]);
    
    // Extract text elements
    const textMatches = code.matchAll(/Text\s*\(\s*"([^"]+)"\s*\)/g);
    const texts = Array.from(textMatches).map(m => m[1]);
    
    // Extract images
    const imageMatches = code.matchAll(/Image\s*\(\s*"([^"]+)"\s*\)/g);
    const images = Array.from(imageMatches).map(m => m[1]);
    
    // Extract text fields
    const textFieldMatches = code.matchAll(/TextField\s*\(\s*"([^"]+)"\s*,\s*text:/g);
    const textFields = Array.from(textFieldMatches).map(m => m[1]);
    
    return {
      structName,
      buttons,
      texts,
      images,
      textFields
    };
  };

  const viewInfo = extractViewInfo(swiftCode);

  return (
    <div className="h-full bg-gray-900 p-4 overflow-y-auto">
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-6 min-h-[400px]">
        <div className="mb-4 pb-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">{viewInfo.structName}</h3>
        </div>
        
        <div className="space-y-4">
          {/* Render Texts */}
          {viewInfo.texts.map((text, index) => (
            <div key={`text-${index}`} className="text-gray-800">
              {text}
            </div>
          ))}
          
          {/* Render TextFields */}
          {viewInfo.textFields.map((placeholder, index) => (
            <input
              key={`textfield-${index}`}
              type="text"
              placeholder={placeholder}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-800 placeholder-gray-400"
              disabled
            />
          ))}
          
          {/* Render Buttons */}
          {viewInfo.buttons.map((title, index) => (
            <button
              key={`button-${index}`}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              disabled
            >
              {title}
            </button>
          ))}
          
          {/* Render Images */}
          {viewInfo.images.map((imageName, index) => (
            <div
              key={`image-${index}`}
              className="w-full h-32 bg-gray-200 rounded-md flex items-center justify-center text-gray-500 text-sm"
            >
              Image: {imageName}
            </div>
          ))}
          
          {/* If no elements found, show message */}
          {viewInfo.texts.length === 0 && 
           viewInfo.textFields.length === 0 && 
           viewInfo.buttons.length === 0 && 
           viewInfo.images.length === 0 && (
            <div className="text-center text-gray-500 py-8">
              <p className="text-sm">Preview not available</p>
              <p className="text-xs mt-2">Complex SwiftUI views require a Swift runtime to render accurately.</p>
            </div>
          )}
        </div>
        
        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            This is a simplified preview. For accurate rendering, use Xcode Preview.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SwiftUIPreview;
